"use client"

import * as React from "react"

/**
 * A region that is a click target but cannot be a `<button>`.
 *
 * The sites this replaces were all the same `<div onClick>`: fine with a
 * pointer, invisible to everyone else. It takes no focus, so it never
 * reaches the tab order, and Enter and Space do nothing if it somehow
 * does.
 *
 * There was already an `activateProps()` helper for this, spread beside
 * the `onClick`. It worked at runtime and failed in two ways that
 * matter. A spread is opaque to static analysis, so every fixed site
 * still reported as unreachable and nobody could tell the fixed ones
 * from the broken ones. And because the handler had to be written twice
 * — once for the spread, once for `onClick` — several sites drifted into
 * running two different handlers for mouse and keyboard.
 *
 * Here the semantics are literal attributes on one element and the
 * handler is named once, so there is no version of this that is wired up
 * halfway.
 *
 * **Prefer a real `<button>` or `<a>`.** This is for the cases where the
 * element cannot be one: a table row, a card whose layout a button
 * disturbs, a region that is a click target on top of its own content.
 *
 * **Do not use it around something that already contains a control.**
 * Two nested focus stops give the keyboard two stops where the pointer
 * has one, and a screen reader reads the outer name over the inner one.
 * Leave the click as a mouse convenience there and let the inner control
 * do the work.
 */
type ClickableRowProps<E extends HTMLElement> = Omit<
  React.HTMLAttributes<E>,
  "onClick" | "onKeyDown"
> & {
  /** The element to render. Pick the one the layout needs. */
  as?: "div" | "span" | "li" | "td" | "section" | "article"
  ref?: React.Ref<E>
  /** Only needed when the content does not already name the action. */
  label?: string
  /**
   * Declared as a method so TypeScript compares the event parameter
   * bivariantly: a call site that types its handler for the concrete
   * element it renders should not have to widen it.
   *
   * Optional, because several call sites pass a handler that may be
   * absent. When it is, the element renders inert rather than
   * advertising a control that does nothing.
   */
  onActivate?(event: React.MouseEvent<E> | React.KeyboardEvent<E>): void
  // Callers pass `data-slot` and friends straight through.
  [dataAttribute: `data-${string}`]: unknown
}

export function ClickableRow<E extends HTMLElement = HTMLDivElement>({
  as: Tag = "div",
  onActivate,
  label,
  children,
  ...rest
}: ClickableRowProps<E>) {
  const interactive = onActivate
    ? {
        role: "button" as const,
        tabIndex: 0,
        "aria-label": label,
        onClick: onActivate,
        onKeyDown: (event: React.KeyboardEvent<E>) => {
          if (event.key !== "Enter" && event.key !== " ") return
          // Only when this element itself has focus: a keypress inside a
          // nested input would otherwise activate the wrapper too.
          if (event.target !== event.currentTarget) return
          // Space would scroll the page before the handler ran.
          event.preventDefault()
          onActivate(event)
        },
      }
    : {}

  const Element = Tag as React.ElementType
  return (
    <Element {...rest} {...interactive}>
      {children}
    </Element>
  )
}
